import {
  Container,
  Center,
  Loader,
  Paper,
  Button,
  Text,
  NumberFormatter,
  Badge,
  Group,
  TextInput,
  Grid,
  Stack,
  Select,
  NumberInput,
  Switch,
  // useComputedColorScheme,
} from "@mantine/core";
import { useEffect, useState } from "react";
import { useForm } from "@mantine/form";
import { customerPortal, getTenantProfile, updateTenantProfile } from "../api";
import ApiKeys from "./ApiKeys";
import { notifications } from "@mantine/notifications";
import { Link } from "wouter";
import { useTranslation } from "react-i18next";

export default function TenantProfileEdit({
  showSubscription = true,
  showCredits = true,
  // Apps with their own dedicated API-keys page (e.g. Veft) pass false to avoid a second,
  // ungated API-keys surface here.
  showApiKeys = true,
}) {
  const [profile, setProfile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useTranslation();

  showCredits = showCredits && !!profile?.plan;
  // console.log("profile.plan:", profile?.plan, showCredits);

  // var theme = useComputedColorScheme();

  const form = useForm({
    initialValues: {
      name: "",
    },
  });

  const autoRefillForm = useForm({
    initialValues: {
      creditAutoRefillThreshold: null,
      creditAutoRefillType: "default",
    },
  });

  useEffect(() => {
    getTenantProfile().then((data) => {
      var formInit = { name: data?.name || "" };
      form.setValues(formInit);
      autoRefillForm.setValues({
        creditAutoRefillThreshold: data?.creditAutoRefillThreshold || null,
        creditAutoRefillType: data?.creditAutoRefillType || "default",
      });
      setProfile(data);
    });
  }, []);

  if (!profile)
    return (
      <Container size="sm">
        <Center>
          <Loader />
        </Center>
      </Container>
    );

  return (
    <Container size="sm">
      <Grid>
        <Grid.Col span={12}>
          <Paper radius="md" p="lg">
            <Text mb="sm" fz="xl" fw={500}>
              {t("tenant.settings_title", "Settings")}
            </Text>
            <form
              onSubmit={form.onSubmit((values) => {
                updateTenantProfile(values).then((data) => {
                  setProfile(data);
                  notifications.show({
                    title: t("tenant.profile_updated_title", "Profile updated"),
                    message: t(
                      "tenant.profile_updated_message",
                      "Your tenant profile has been updated",
                    ),
                    color: "teal",
                  });
                });
              })}
            >
              <TextInput
                label={t("tenant.org_name_label", "Organization Name")}
                placeholder={t("tenant.org_name_placeholder", "ACME, Inc.")}
                required
                {...form.getInputProps("name")}
              />

              <Group mt="md" justify="flex-end">
                <Button variant="outline" type="submit">
                  {t("generic.action_save", "Save")}
                </Button>
              </Group>
            </form>
          </Paper>
        </Grid.Col>

        <Grid.Col span={12}>
          <Paper radius="md" p="lg">
            <Text mb="sm" fz="xl" fw={500}>
              {t("tenant.security_title", "Security")}
            </Text>
            <Switch
              checked={!!profile.requirePasskey}
              disabled={isLoading}
              onChange={(e) => {
                const requirePasskey = e.currentTarget.checked;
                setIsLoading(true);
                // Send the full profile so toggling security doesn't clear the credit settings
                // (RequirePasskey is applied server-side only when present).
                updateTenantProfile({
                  name: profile.name,
                  creditAutoRefillThreshold: profile.creditAutoRefillThreshold ?? null,
                  creditAutoRefillType: profile.creditAutoRefillType ?? null,
                  requirePasskey,
                })
                  .then((data) => {
                    setProfile(data);
                    notifications.show({
                      title: t("tenant.security_updated_title", "Security settings updated"),
                      message: requirePasskey
                        ? t(
                            "tenant.require_passkey_on_msg",
                            "Passkey sign-in is now required for all members.",
                          )
                        : t(
                            "tenant.require_passkey_off_msg",
                            "Passkey sign-in is no longer required.",
                          ),
                      color: "teal",
                    });
                  })
                  .finally(() => setIsLoading(false));
              }}
              label={t("tenant.require_passkey_label", "Require passkey sign-in")}
              description={t(
                "tenant.require_passkey_desc",
                "Every member must sign in with a passkey. Members who don't have one yet are prompted to create one the next time they sign in; password-only sign-in is then refused. Make sure you can create a passkey on your device before turning this on.",
              )}
            />
          </Paper>
        </Grid.Col>

        {showCredits && (
          <>
            <Grid.Col span={12}>
              <Paper radius="md" p="lg">
                <Text mb="sm" fz="xl" fw={500}>
                  {t("tenant.credits_title", "Credits")}
                </Text>
                <Stack>
                  <Group justify="space-between">
                    <div>
                      {profile.creditTotals?.map((credit) => (
                        <Group key={credit.creditType}>
                          <Badge radius="xs" variant="dot" color="grape">
                            {credit.creditType}
                          </Badge>
                          <Text fz="lg">
                            <NumberFormatter
                              value={credit.creditsLeft}
                              decimalScale={0}
                              thousandSeparator
                            />
                          </Text>
                        </Group>
                      ))}
                    </div>

                    <Button
                      variant="outline"
                      loading={isLoading}
                      component={Link}
                      to="/credits"
                    >
                      {t("tenant.buy_credits", "Buy credits")}
                    </Button>
                  </Group>
                </Stack>
              </Paper>
            </Grid.Col>

            <Grid.Col span={12}>
              <Paper radius="md" p="lg">
                <Text mb="sm" fz="xl" fw={500}>
                  {t("tenant.auto_refill_title", "Auto-Refill")}
                </Text>
                <Text size="sm" c="dimmed" mb="md">
                  {t(
                    "tenant.auto_refill_desc",
                    "Automatically top up your credits to a set threshold at each billing cycle. Only available with an active subscription.",
                  )}
                </Text>

                <form
                  onSubmit={autoRefillForm.onSubmit((values) => {
                    setIsLoading(true);
                    const payload = {
                      name: profile.name,
                      creditAutoRefillThreshold:
                        values.creditAutoRefillThreshold
                          ? parseInt(values.creditAutoRefillThreshold)
                          : null,
                      creditAutoRefillType: values.creditAutoRefillType,
                    };
                    updateTenantProfile(payload)
                      .then((data) => {
                        setProfile(data);
                        autoRefillForm.setValues({
                          creditAutoRefillThreshold:
                            data.creditAutoRefillThreshold || null,
                          creditAutoRefillType:
                            data.creditAutoRefillType || "default",
                        });
                        notifications.show({
                          title: t(
                            "tenant.auto_refill_updated_title",
                            "Auto-refill updated",
                          ),
                          message: t(
                            "tenant.auto_refill_updated_message",
                            "Your auto-refill settings have been saved",
                          ),
                          color: "teal",
                        });
                      })
                      .finally(() => {
                        setIsLoading(false);
                      });
                  })}
                >
                  <NumberInput
                    label={t("tenant.refill_threshold_label", "Refill Threshold")}
                    placeholder={t(
                      "tenant.refill_threshold_placeholder",
                      "Enter target credit balance",
                    )}
                    description={t(
                      "tenant.refill_threshold_desc",
                      "Your balance will be topped up to this amount each billing cycle",
                    )}
                    min={0}
                    {...autoRefillForm.getInputProps(
                      "creditAutoRefillThreshold"
                    )}
                  />

                  <Select
                    label={t("tenant.credit_type_label", "Credit Type")}
                    description={t(
                      "tenant.credit_type_desc",
                      "Which type of credits to refill",
                    )}
                    {...autoRefillForm.getInputProps("creditAutoRefillType")}
                    data={[
                      { value: "default", label: t("tenant.credit_type_default", "Default") },
                      // { value: "premium", label: "Premium" },
                      // { value: "standard", label: "Standard" },
                    ]}
                    mt="md"
                  />

                  <Group mt="md" justify="flex-end">
                    <Button type="submit" loading={isLoading}>
                      {t("tenant.save_auto_refill", "Save Auto-Refill Settings")}
                    </Button>
                    {profile?.creditAutoRefillThreshold && (
                      <Button
                        variant="outline"
                        color="red"
                        loading={isLoading}
                        onClick={() => {
                          setIsLoading(true);
                          const payload = {
                            name: profile.name,
                            creditAutoRefillThreshold: null,
                            creditAutoRefillType: null,
                          };
                          updateTenantProfile(payload)
                            .then((data) => {
                              setProfile(data);
                              autoRefillForm.setValues({
                                creditAutoRefillThreshold: null,
                                creditAutoRefillType: "default",
                              });
                              notifications.show({
                                title: t(
                                  "tenant.auto_refill_disabled_title",
                                  "Auto-refill disabled",
                                ),
                                message: t(
                                  "tenant.auto_refill_disabled_message",
                                  "Auto-refill has been turned off",
                                ),
                                color: "blue",
                              });
                            })
                            .finally(() => {
                              setIsLoading(false);
                            });
                        }}
                      >
                        {t("tenant.disable_auto_refill", "Disable Auto-Refill")}
                      </Button>
                    )}
                  </Group>

                  {profile?.creditAutoRefillThreshold && (
                    <Text mt="md" size="sm" c="teal">
                      ✓ Auto-refill is enabled: Your{" "}
                      {profile.creditAutoRefillType} credits will be topped up
                      to{" "}
                      <NumberFormatter
                        value={profile.creditAutoRefillThreshold}
                        thousandSeparator
                      />{" "}
                      at each billing cycle. Currently, this would equal{" "}
                      <Text component="span" fw={700}>
                        <NumberFormatter
                          value={
                            profile.creditAutoRefillThreshold -
                            (profile.creditTotals?.find(
                              (c) =>
                                c.creditType === profile.creditAutoRefillType
                            )?.creditsLeft || 0)
                          }
                          thousandSeparator
                        />
                      </Text>{" "}
                      credits to be added on next cycle for{" "}
                      <Text component="span" fw={700}>
                        <NumberFormatter
                          suffix=" USD"
                          decimalScale={2}
                          fixedDecimalScale
                          value={
                            0.003 *
                            (profile.creditAutoRefillThreshold -
                              (profile.creditTotals?.find(
                                (c) =>
                                  c.creditType === profile.creditAutoRefillType
                              )?.creditsLeft || 0))
                          }
                          thousandSeparator
                        />
                      </Text>{" "}
                    </Text>
                  )}
                </form>
              </Paper>
            </Grid.Col>
          </>
        )}

        <Grid.Col span={12}>
          <Paper radius="md" p="lg">
            <Text mb="sm" fz="xl" fw={500}>
              {t("tenant.users_title", "Users")}
            </Text>

            <Group mt="md" justify="flex-end">
              <Button variant="outline" component={Link} to="/users">
                {t("tenant.manage_users", "Manage users")}
              </Button>
            </Group>
          </Paper>
        </Grid.Col>

        {showSubscription && (
          <Grid.Col span={12}>
            <Paper radius="md" p="lg">
              <Text mb="sm" fz="xl" fw={500}>
                {t("tenant.subscription_title", "Subscription")}
              </Text>
              <Stack>
                <Text>
                  {t("tenant.current_plan", "Current plan:")}{" "}
                  {profile.plan
                    ? profile.plan
                    : t(
                        "tenant.no_subscription",
                        "You are not currently subscribed to any plan",
                      )}
                </Text>
                <Group justify="flex-end">
                  {profile.plan ? (
                    <Button
                      loading={isLoading}
                      variant="outline"
                      onClick={() => {
                        setIsLoading(true);
                        customerPortal()
                          .then((data) => {
                            window.location = data.redirectUrl;
                          })
                          .catch(() => {
                            // No Stripe billing profile yet (e.g. a plan granted manually,
                            // without a subscription). Route to /plans to start a real
                            // checkout instead of dead-ending on a portal that can't open.
                            window.location = "/plans";
                          })
                          .finally(() => {
                            setIsLoading(false);
                          });
                      }}
                    >
                      {t("tenant.manage_subscription", "Manage subscription")}
                    </Button>
                  ) : (
                    <Button
                      loading={isLoading}
                      variant="filled"
                      component={Link}
                      to="/plans"
                    >
                      {t("tenant.create_subscription", "Create subscription")}
                    </Button>
                  )}
                </Group>
              </Stack>
            </Paper>
          </Grid.Col>
        )}

        {showApiKeys && (
          <Grid.Col span={12}>
            <Paper withBorder mt="xl" p="sm" radius="md">
              <ApiKeys />
            </Paper>
          </Grid.Col>
        )}
      </Grid>
    </Container>
  );
}
